import { useEffect, useRef, useState } from "react";
import { FiChevronsDown, FiLogOut, FiUser } from "react-icons/fi";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { MdOutlineRequestQuote } from "react-icons/md";
import {
  RiBuilding4Line,
  RiDashboardLine,
  RiUserSearchLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import logo from "../../../assets-admin/logo/logo.png";
import { useAuth } from "../../../context/AuthContext";
import { Z_INDEX } from "../../../utils/constants";
import { IoClose } from "react-icons/io5";

const AdminNavBar = ({ handleHamburgerClick, isSidebarOpen }) => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const { user, adminUser, logout, adminLogout } = useAuth();
  const userDropdownRef = useRef(null);

  const isSuperAdminContext = window.location.pathname.startsWith("/super-admin");
  const currentUser = isSuperAdminContext ? adminUser : user;

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const handleSignIn = () => {
    navigate(isSuperAdminContext ? "/super-admin/login" : "/login");
    setOpenMenu(null);
  };
  const handleItemClick = (item) => {
    navigate(item?.path);
    setOpenMenu(null);
  };

  const handleSignOut = async () => {
    if (isSuperAdminContext) {
      adminLogout();
    } else {
      logout();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore Hamburgers
      if (event.target.closest('[data-hamburger="true"]')) return;

      // Ignore clicks inside dropdown
      if (userDropdownRef.current?.contains(event.target)) return;

      // Ignore clicks on avatar icon
      if (event.target.closest('[data-user-menu="true"]')) return;

      setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sidebarMenuItems = [
    {
      icon: FiUser,
      label: "My Profile",
      path: "/admin/profile-subscription",
    },
    {
      icon: RiDashboardLine,
      label: "Dashboard",
      path: "/admin",
    },
    {
      icon: MdOutlineRequestQuote,
      label: "Requested Properties",
      path: "/admin/requests",
    },
    {
      icon: RiBuilding4Line,
      label: "My Properties",
      path: "/admin/properties",
    },
    {
      icon: RiUserSearchLine,
      label: "Lead / Response",
      path: "/admin/leads",
    },
  ];

  return (
    <header
      style={{ zIndex: Z_INDEX.navbar }}
      className="flex items-center justify-between bg-[#0C3861] px-3 sm:px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20 shadow-md fixed w-full"
    >
      {/* Left: Logo & Title */}
      <div
        onClick={() => navigate(`/`)}
        className="flex items-center select-none cursor-pointer space-x-1.5 sm:space-x-2 h-full"
      >
        <img
          src={logo}
          alt="Logo"
          className="h-10 sm:h-12 md:h-full object-contain cursor-pointer"
        />
        <div className="flex flex-col cursor-default items-center leading-[0.9]">
          <span className="text-white font-audiowide font-medium text-lg sm:text-xl md:text-3xl tracking-wide">
            LeadBook
          </span>
          <div
            className="hidden md:flex font-poppins text-[#999999] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking- justify-between w-full"
            style={{
              marginTop: "-6px",
              letterSpacing: "0.3em ",
              textAlign: "justify",
            }}
          >
            <span className="w-full text-center">A Path To Success</span>
          </div>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4">
        {/* Icons - Avatar and Menu with Dropdown */}
        <div className="relative">
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 cursor-pointer">
            <div
              data-user-menu="true"
              onClick={() => toggleMenu("user")}
              className="hidden sm:flex items-center gap-2"
            >
              <button className="w-8 h-8 mx-1.5 sm:mx-0 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm sm:text-base">
                {(currentUser?.fullName || currentUser?.name)?.[0]?.toUpperCase() || "G"}
              </button>
              <FiChevronsDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                  openMenu === "user" ? "rotate-180" : ""
                }`}
              />
            </div>

            {/* Mobile avatar without dropdown icon */}
            <button
              data-user-menu="true"
              onClick={() => toggleMenu("user")}
              className="sm:hidden w-8 h-8 mx-1.5 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm"
            >
              {(currentUser?.fullName || currentUser?.name)?.[0]?.toUpperCase() || "G"}
            </button>

            <div
              className="sm:hidden relative flex items-center justify-center w-8 h-8"
              onClick={() => handleHamburgerClick && handleHamburgerClick()}
              data-hamburger="true"
            >
              {/* Hamburger Icon */}
              <HiOutlineMenuAlt3
                strokeWidth={2.5}
                className={`absolute text-white text-2xl transition-all duration-300 ${
                  isSidebarOpen
                    ? "opacity-0 scale-50 rotate-90"
                    : "opacity-100 scale-100 rotate-0"
                }`}
                data-hamburger="true"
              />

              {/* Close Icon */}
              <IoClose
                className={`absolute text-white text-3xl transition-all duration-300
                      ${
                        isSidebarOpen
                          ? "opacity-100 scale-100 rotate-0"
                          : "opacity-0 scale-50 rotate-90"
                      }
                    `}
                data-hamburger="true"
              />
            </div>
          </div>

          {/* User Profile Dropdown */}
          {openMenu === "user" && (
            <div
              ref={userDropdownRef}
              style={{ pointerEvents: "auto", zIndex: 9999 }}
              className={`absolute right-0 mt-2 w-60 lg:w-72 bg-white rounded-xl shadow-lg border border-gray-200
      py-3 transform origin-top-right transition-all duration-200 ease-out
      ${openMenu === "user" ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
            >
              {/* User Info Section */}
              <div className="px-5 pb-3 border-b border-gray-200 flex items-center space-x-3">
                <button className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base shadow-sm">
                  {(currentUser?.fullName || currentUser?.name)?.[0]?.toUpperCase() || "G"}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {currentUser?.fullName || currentUser?.name || "Guest User"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {currentUser?.email || "Not logged in"}
                  </p>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1.5">
                {sidebarMenuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleItemClick(item)}
                    className="w-full cursor-pointer flex items-center justify-between px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                      <span className="font-medium text-sm">{item.label}</span>
                    </div>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full font-semibold shadow-sm">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-1"></div>

              {/* Sign Out / Sign In */}
              {currentUser ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-red-600 hover:bg-red-50/80 transition-all duration-200 group w-full"
                >
                  <FiLogOut className="h-4.5 w-4.5 text-gray-500 group-hover:text-red-600 transition-colors" />
                  <span className="font-medium text-sm">Sign Out</span>
                </button>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group w-full"
                >
                  <FiUser className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                  <span className="font-medium text-sm">Sign In</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminNavBar;

// import { useNavigate } from "react-router-dom";
// import { useEffect, useRef, useState } from "react";
// import logo from "../../../assets-admin/logo/logo.png";
// import post_property_icon from "../../../assets-admin/icons/post_property_icon.png";
// import { BiChevronDown } from "react-icons/bi";
// import { HiOutlineMenuAlt3 } from "react-icons/hi";
// import { FiChevronsDown, FiLogOut, FiUser } from "react-icons/fi";
// import { MdOutlineRequestQuote } from "react-icons/md";
// import {
//   RiBuilding4Line,
//   RiDashboardLine,
//   RiUserSearchLine,
// } from "react-icons/ri";
// import { Z_INDEX } from "../../../utils/constants";
// import { useAuth } from "../../../context/AuthContext";

// const AdminNavBar = ({ handleHamburgerClick }) => {
//   const navigate = useNavigate();
//   const [openMenu, setOpenMenu] = useState(null);
//   const { user, logout } = useAuth();
//   const closeTimer = useRef(null);
//   const dropdownRef = useRef(null);
//   const userDropdownRef = useRef(null);

//   const toggleMenu = (menu) => {
//     setOpenMenu(openMenu === menu ? null : menu);
//   };

//   const handleMouseEnter = (menu) => {
//     if (closeTimer.current) clearTimeout(closeTimer.current);
//     setOpenMenu(menu);
//   };

//   const handleMouseLeave = () => {
//     closeTimer.current = setTimeout(() => setOpenMenu(null), 200);
//   };

//   const handleSignIn = () => {
//     // console.log("clicked login");
//     navigate("/login");
//     setOpenMenu(null);
//   };
//   const handleItemClick = (item) => {
//     navigate(item?.path);
//     setOpenMenu(null);
//   };

//   const handleSignOut = async () => {
//     logout();
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       // Check if click is on hamburger menu
//       if (event.target.closest('[data-hamburger="true"]')) {
//         return;
//       }

//       // Check if click is inside the user dropdown
//       if (
//         userDropdownRef.current &&
//         userDropdownRef.current.contains(event.target)
//       ) {
//         return;
//       }

//       // Check if click is on user avatar/dropdown trigger
//       if (event.target.closest('[data-user-menu="true"]')) {
//         return;
//       }

//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setOpenMenu(null);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const buyOptions = ["Real Estate", "Stocks", "Gold", "Crypto"];
//   const sellOptions = ["Cars", "Land", "NFTs", "Art"];

//   const sidebarMenuItems = [
//     {
//       icon: FiUser,
//       label: "My Profile",
//       path: "/admin/profile",
//     },
//     {
//       icon: RiDashboardLine,
//       label: "Dashboard",
//       path: "/admin",
//     },
//     {
//       icon: MdOutlineRequestQuote,
//       label: "Requested Properties",
//       path: "/admin/requests",
//     },
//     {
//       icon: RiBuilding4Line,
//       label: "My Properties",
//       path: "/admin/properties",
//     },
//     {
//       icon: RiUserSearchLine,
//       label: "Lead / Response",
//       path: "/admin/leads",
//     },
//   ];

//   // const handlePostProperty = () => navigate("/post-property");
//   const handlePostProperty = () => {};

//   return (
//     <header
//       style={{ zIndex: Z_INDEX.navbar }}
//       className="flex items-center justify-between bg-[#0C3861] px-3 sm:px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20 shadow-md fixed w-full"
//     >
//       {/* Left: Logo & Title */}
//       <div
//         onClick={() => navigate(`/`)}
//         className="flex items-center select-none cursor-pointer space-x-1.5 sm:space-x-2 h-full"
//       >
//         <img
//           src={logo}
//           alt="Logo"
//           className="h-10 sm:h-12 md:h-full object-contain cursor-pointer"
//         />
//         <div className="flex flex-col cursor-default items-center leading-[0.9]">
//           <span className="text-white font-audiowide font-medium text-lg sm:text-xl md:text-3xl tracking-wide">
//             LeadBook
//           </span>
//           <div
//             className="hidden md:flex font-poppins text-[#999999] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking- justify-between w-full"
//             style={{
//               marginTop: "-6px",
//               letterSpacing: "0.3em ",
//               textAlign: "justify",
//             }}
//           >
//             <span className="w-full text-center">A Path To Success</span>
//           </div>
//         </div>
//       </div>

//       {/* Right: Actions */}
//       <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4">
//         {/* Center: Navigation Links - Hidden on mobile */}
//         <div
//           ref={dropdownRef}
//           className="hidden lg:flex space-x-4 xl:space-x-6 text-white relative"
//         >
//           {/* BUY DROPDOWN */}
//           <div
//             style={{ zIndex: Z_INDEX.dropdown }}
//             className="relative"
//             onMouseEnter={() => handleMouseEnter("buy")}
//             onMouseLeave={handleMouseLeave}
//           >
//             <button
//               onClick={() => toggleMenu("buy")}
//               className={`flex items-center gap-1 text-sm xl:text-base transition ${
//                 openMenu === "buy" ? "text-gray-300" : "hover:text-gray-300"
//               }`}
//             >
//               Buy
//               <BiChevronDown
//                 className={`text-lg transition-transform duration-200 ${
//                   openMenu === "buy" ? "rotate-180 opacity-60" : ""
//                 }`}
//               />
//             </button>

//             <div
//               className={`absolute left-0 mt-2 w-44 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 origin-top ${
//                 openMenu === "buy"
//                   ? "scale-100 opacity-100 visible"
//                   : "scale-95 opacity-0 invisible"
//               }`}
//             >
//               {buyOptions.map((item) => (
//                 <div
//                   key={item}
//                   className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition text-sm"
//                 >
//                   {item}
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* SELL DROPDOWN */}
//           <div
//             className="relative"
//             style={{ zIndex: Z_INDEX.dropdown }}
//             onMouseEnter={() => handleMouseEnter("sell")}
//             onMouseLeave={handleMouseLeave}
//           >
//             <button
//               onClick={() => toggleMenu("sell")}
//               className={`flex items-center gap-1 text-sm xl:text-base transition ${
//                 openMenu === "sell" ? "text-gray-300" : "hover:text-gray-300"
//               }`}
//             >
//               Sell
//               <BiChevronDown
//                 className={`text-lg transition-transform duration-200 ${
//                   openMenu === "sell" ? "rotate-180 opacity-60" : ""
//                 }`}
//               />
//             </button>

//             <div
//               className={`absolute left-0 mt-2 w-44 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 origin-top ${
//                 openMenu === "sell"
//                   ? "scale-100 opacity-100 visible"
//                   : "scale-95 opacity-0 invisible"
//               }`}
//             >
//               {sellOptions.map((item) => (
//                 <div
//                   key={item}
//                   className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition text-sm"
//                 >
//                   {item}
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>

//         <button
//           onClick={() => {
//             window.location.href = "/interior";
//             window.scrollTo({ top: 0, behavior: "smooth" });
//           }}
//           className={`hidden lg:flex items-center gap-1 text-sm xl:text-base transition text-gray-300 hover:text-gray-300}`}
//         >
//           Interior
//         </button>

//         {/* Post Property Button - Icon-only on small screens */}
//         <button
//           onClick={handlePostProperty}
//           className="flex items-center justify-center gap-1.5 md:gap-2 bg-btn-brandRed  hover:bg-btn-brandRedHover
//         px-3 md:px-4 lg:px-5 py-1.5 sm:py-2 md:py-2 text-white font-normal text-sm md:text-base shadow-md hover:shadow-lg
//         transform-gpu transition-all duration-100 ease-out rounded-full
//         active:scale-[0.97] active:shadow-sm outline-none focus-visible:ring-2
//         select-none cursor-pointer"
//         >
//           <span className="w-[1.38rem] h-[1.38rem] md:w-5 md:h-5 lg:w-6 lg:h-6 flex flex-shrink-0 select-none items-center justify-center">
//             <img
//               src={post_property_icon}
//               alt="post-property"
//               style={{ zIndex: Z_INDEX.base }}
//               className="w-full h-full object-contain pointer-events-none select-none transform-gpu"
//               draggable="false"
//             />
//           </span>
//           <span className="hidden sm:inline transform-gpu">Post Property</span>
//         </button>

//         {/* Icons - Avatar and Menu with Dropdown */}
//         <div className="relative">
//           <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 cursor-pointer">
//             <div
//               data-user-menu="true"
//               onClick={() => toggleMenu("user")}
//               className="hidden sm:flex items-center gap-2"
//             >
//               <button className="w-8 h-8 mx-1.5 sm:mx-0 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm sm:text-base">
//                 {user?.fullName?.[0]?.toUpperCase() || "G"}
//               </button>
//               <FiChevronsDown
//                 className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
//                   openMenu === "user" ? "rotate-180" : ""
//                 }`}
//               />
//             </div>

//             {/* Mobile avatar without dropdown icon */}
//             <button
//               data-user-menu="true"
//               onClick={() => toggleMenu("user")}
//               className="sm:hidden w-8 h-8 mx-1.5 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm"
//             >
//               {user?.fullName?.[0]?.toUpperCase() || "G"}
//             </button>

//             <div
//               className="sm:hidden"
//               onClick={() => handleHamburgerClick && handleHamburgerClick()}
//               data-hamburger="true"
//             >
//               <HiOutlineMenuAlt3
//                 strokeWidth={2.5}
//                 className="text-white text-2xl sm:text-xl md:text-2xl lg:text-xl cursor-pointer w-8 h-8"
//                 data-hamburger="true"
//               />
//             </div>
//           </div>

//           {/* User Profile Dropdown */}
//           {openMenu === "user" && (
//             <div
//               ref={userDropdownRef}
//               style={{ pointerEvents: "auto", zIndex: 9999 }}
//               className={`absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200
//       py-3 transform origin-top-right transition-all duration-200 ease-out
//       ${openMenu === "user" ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
//             >
//               {/* User Info Section */}
//               <div className="px-5 pb-3 border-b border-gray-200 flex items-center space-x-3">
//                 <button className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base shadow-sm">
//                   {user?.fullName?.[0]?.toUpperCase() || "G"}
//                 </button>
//                 <div className="flex-1 min-w-0">
//                   <p className="text-sm font-semibold text-gray-900 truncate">
//                     {user?.fullName || "Guest User"}
//                   </p>
//                   <p className="text-xs text-gray-500 truncate">
//                     {user?.email || "Not logged in"}
//                   </p>
//                 </div>
//               </div>

//               {/* Menu Items */}
//               <div className="py-1.5">
//                 {sidebarMenuItems.map((item, index) => (
//                   <button
//                     key={index}
//                     onClick={() => handleItemClick(item)}
//                     className="w-full cursor-pointer flex items-center justify-between px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group"
//                   >
//                     <div className="flex items-center space-x-3">
//                       <item.icon className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
//                       <span className="font-medium text-sm">{item.label}</span>
//                     </div>
//                     {item.badge && (
//                       <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full font-semibold shadow-sm">
//                         {item.badge}
//                       </span>
//                     )}
//                   </button>
//                 ))}
//               </div>

//               {/* Divider */}
//               <div className="border-t border-gray-200 my-1"></div>

//               {/* Sign Out / Sign In */}
//               {user ? (
//                 <button
//                   onClick={handleSignOut}
//                   className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-red-600 hover:bg-red-50/80 transition-all duration-200 group w-full"
//                 >
//                   <FiLogOut className="h-4.5 w-4.5 text-gray-500 group-hover:text-red-600 transition-colors" />
//                   <span className="font-medium text-sm">Sign Out</span>
//                 </button>
//               ) : (
//                 <button
//                   onClick={handleSignIn}
//                   className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group w-full"
//                 >
//                   <FiUser className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
//                   <span className="font-medium text-sm">Sign In</span>
//                 </button>
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     </header>
//   );
// };

// export default AdminNavBar;
